import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { MapPin, Sparkles, GraduationCap, Send, ArrowRight, Building2 } from "lucide-react";

export default function Landing() {
  const { lang } = useAuth();
  return (
    <Layout>
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#F97316] mb-4">
              {t(lang, "brand_tagline")}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-[#1E3A8A] leading-[1.05]">
              {t(lang, "hero_title")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {t(lang, "hero_sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/map">
                <Button data-testid="hero-cta-explore" className="bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full px-6 h-12">
                  {t(lang, "cta_explore")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="hero-cta-signup" variant="outline" className="rounded-full px-6 h-12 border-slate-300">
                  {t(lang, "cta_signup")}
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span>🇪🇺 Europe</span>
              <span>🌎 {lang === "fr" ? "Amériques" : lang === "es" ? "Américas" : lang === "de" ? "Amerika" : "Americas"}</span>
              <span>🌏 Asia</span>
              <span>🌍 Africa</span>
              <span className="ml-auto text-slate-400 text-[10px]">FR · EN · ES · DE</span>
            </div>
          </div>

          <div className="relative fade-up">
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#F97316]/20 blur-2xl" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-[#1E3A8A]/20 blur-2xl" />
            <div className="relative rounded-3xl bg-white border border-slate-200 shadow-xl p-6 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1586449480537-3a22cf98b04c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxpbnRlcmFjdGl2ZSUyMG1hcCUyMGNpdHl8ZW58MHx8fHwxNzg1MTI1NTAyfDA&ixlib=rb-4.1.0&q=85" alt="Map" className="rounded-2xl h-72 w-full object-cover" />
              <div className="mt-4 flex gap-2 items-center">
                <span className="px-3 py-1 rounded-full bg-[#FFF7ED] text-[#C2410C] text-xs font-semibold">95% Match</span>
                <span className="text-sm text-slate-600">TechFlow SAS · Paris</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: MapPin, title: t(lang, "nav_map"), desc: lang === "fr" ? "Explorez les entreprises locales sur une carte mondiale." : "Explore local companies on a world map." },
            { icon: Sparkles, title: t(lang, "nav_suggestions"), desc: lang === "fr" ? "Matching IA par diplôme avec % de compatibilité." : "AI matching by degree with % compatibility." },
            { icon: GraduationCap, title: t(lang, "nav_internships"), desc: lang === "fr" ? "Entreprises qui accueillent régulièrement des stagiaires." : "Companies that regularly host interns." },
            { icon: Send, title: t(lang, "nav_applications"), desc: lang === "fr" ? "Postulez spontanément en un clic." : "Apply spontaneously in one click." },
          ].map((f, i) => (
            <div key={`feature-${f.title}`} data-testid={`feature-card-${i}`} className="rounded-2xl bg-white border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-md transition-transform">
              <div className="w-10 h-10 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center text-[#1E3A8A] mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-[#1E3A8A] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Building2 className="w-8 h-8 mb-4 text-[#F97316]" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                {t(lang, "landing_pme_title")}
              </h2>
              <p className="mt-3 text-white/80 max-w-lg leading-relaxed">
                {t(lang, "landing_pme_sub")}
              </p>
            </div>
            <div className="flex md:justify-end gap-3 flex-wrap">
              <Link to="/claim">
                <Button data-testid="cta-claim" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full h-12 px-6">
                  {t(lang, "landing_pme_cta")}
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="cta-company" variant="outline" className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10">
                  {t(lang, "landing_pme_cta_alt")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        {t(lang, "footer_note")}
      </footer>
    </Layout>
  );
}
